import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Car, Eye } from 'lucide-react';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';

interface VehicleSectionProps {
  vehicles: any[];
  title: string;
  description: string;
  scrollId: string;
  viewAllLink?: string;
  whatsappUrl: string;
  icon?: React.ReactNode;
  maxVehicles?: number;
}

export const VehicleSection = ({
  vehicles,
  title,
  description,
  scrollId,
  viewAllLink,
  whatsappUrl,
  icon,
  maxVehicles = 5
}: VehicleSectionProps) => {
  const displayVehicles = vehicles.slice(0, maxVehicles);
  const remainingCount = vehicles.length - maxVehicles;
  const totalSlots = maxVehicles;
  const placeholdersNeeded = Math.max(0, totalSlots - displayVehicles.length);
  
  // Only show arrows if there are at least 5 vehicles
  const showArrows = vehicles.length >= 5;

  return (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {title}
        </h2>
        <p className="text-muted-foreground">
          {description}
        </p>
      </div>
      {remainingCount > 0 && viewAllLink && (
        <Link to={viewAllLink}>
          <Button variant="outline">
            Ver Todos ({vehicles.length})
          </Button>
        </Link>
      )}
    </div>
  );
};

interface VehicleScrollContainerProps {
  vehicles: any[];
  scrollId: string;
  viewAllLink?: string;
  whatsappUrl: string;
  icon?: React.ReactNode;
  maxVehicles?: number;
  moreText?: string;
  title?: string;
  description?: string;
  hidePlaceholders?: boolean;
}

export const VehicleScrollContainer = ({
  vehicles,
  scrollId,
  viewAllLink,
  whatsappUrl,
  icon = <Car className="h-10 w-10 text-primary" />,
  maxVehicles = 5,
  moreText = 'veículos',
  hidePlaceholders = false,
}: VehicleScrollContainerProps) => {
  const displayVehicles = vehicles.slice(0, maxVehicles);
  const remainingCount = vehicles.length - maxVehicles;
  const placeholdersNeeded = hidePlaceholders ? 0 : Math.max(0, maxVehicles - displayVehicles.length);

  return (
    <div className="relative group/carousel">
        <div 
          id={scrollId} 
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth pb-4"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
        {/* Real Vehicles */}
        {displayVehicles.map((vehicle) => (
          <div key={vehicle.id} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
            <VehicleCard vehicle={vehicle} whatsappUrl={whatsappUrl} />
          </div>
        ))}

        {/* Placeholders */}
        {placeholdersNeeded > 0 && Array.from({ length: placeholdersNeeded }).map((_, index) => (
          <div key={`placeholder-${index}`} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
            <Card className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex flex-col h-full">
              {/* Placeholder Image - Same aspect ratio as VehicleCard */}
              <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted/30 flex items-center justify-center">
                <Eye className="h-8 w-8 text-muted-foreground/30" />
              </div>

              {/* Compact Info Section - Match VehicleCard structure */}
              <CardContent className="p-4 flex flex-col">
                {/* Title and Version - Fixed heights */}
                <div className="mb-3 flex-shrink-0">
                  <div className="h-6 bg-muted/50 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-muted/30 rounded w-1/2 mb-2"></div>
                  {/* Price Skeleton */}
                  <div className="h-7 bg-muted/50 rounded w-2/3"></div>
                </div>

                {/* Details - Single Row with fixed height */}
                <div className="flex items-center gap-2 border-t border-border/50 pt-3 flex-shrink-0 h-8">
                  <div className="h-3 bg-muted/30 rounded w-16"></div>
                  <div className="w-1 h-1 bg-muted/30 rounded-full"></div>
                  <div className="h-3 bg-muted/30 rounded w-12"></div>
                </div>

                {/* CTA Space */}
                <div className="h-4 mt-3 flex-shrink-0"></div>
              </CardContent>
            </Card>
          </div>
        ))}

        {/* Ver Mais Card (only if there are more than maxVehicles) */}
        {remainingCount > 0 && viewAllLink && (
          <Link to={viewAllLink} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
            <Card className="h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 transition-all duration-300 cursor-pointer group border-2 border-dashed border-primary/30 min-h-[300px]">
              <CardContent className="text-center p-8">
                <div className="mb-4">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    {icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">Ver Todos</h3>
                <p className="text-muted-foreground mb-4">
                  +{remainingCount} {moreText}
                </p>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Navigation Arrows - Only show if more than maxVehicles */}
      {vehicles.length > maxVehicles && (
        <>
          <button
            onClick={() => {
              const container = document.getElementById(scrollId);
              if (container) {
                const isMobile = window.innerWidth < 768;
                const scrollAmount = isMobile ? 296 : 316; // 280px/300px + 16px gap
                container.scrollBy({
                  left: -scrollAmount,
                  behavior: 'smooth'
                });
              }
            }}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200"
            aria-label="Anterior"
          >
            <ChevronLeft className="h-5 w-5 text-gray-800" />
          </button>
          <button
            onClick={() => {
              const container = document.getElementById(scrollId);
              if (container) {
                const isMobile = window.innerWidth < 768;
                const scrollAmount = isMobile ? 296 : 316; // 280px/300px + 16px gap
                container.scrollBy({
                  left: scrollAmount,
                  behavior: 'smooth'
                });
              }
            }}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200"
            aria-label="Próximo"
          >
            <ChevronRight className="h-5 w-5 text-gray-800" />
          </button>
        </>
      )}
    </div>
  );
};

